import type { Document } from '../page';

interface DocumentViewerProps {
  document: Document | null;
}

export default function DocumentViewer({ document }: DocumentViewerProps) {
  if (!document) {
    return (
      <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 sticky top-8">
        <div className="text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-file-search-line text-4xl text-gray-400 w-10 h-10 flex items-center justify-center"></i>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Document Selected</h3>
          <p className="text-sm text-gray-600">
            Click on a document to view its details and preview
          </p>
        </div>
      </div>
    );
  }

  const isImage = ['JPG', 'JPEG', 'PNG', 'GIF'].includes(document.type);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 sticky top-8">
      <div className="p-6 border-b border-gray-100">
        <h2 className="text-lg font-bold text-gray-900">Document Preview</h2>
      </div>

      <div className="p-6">
        <div className="mb-6">
          <div className="aspect-[3/4] bg-gray-100 rounded-lg overflow-hidden mb-4">
            {isImage ? (
              <img
                src={document.url}
                alt={document.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center">
                  <i className="ri-file-line text-6xl text-gray-400 w-16 h-16 flex items-center justify-center mx-auto mb-3"></i>
                  <p className="text-sm text-gray-600">Preview not available</p>
                  <p className="text-xs text-gray-500 mt-1">Download to view</p>
                </div>
              </div>
            )}
          </div>

          <h3 className="text-base font-bold text-gray-900 mb-2">{document.name}</h3>
          {document.description && (
            <p className="text-sm text-gray-600 mb-4">{document.description}</p>
          )}

          <div className="space-y-3 mb-6">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Category</span>
              <span className="font-medium text-gray-900">{document.category}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Type</span>
              <span className="font-medium text-gray-900">{document.type}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Size</span>
              <span className="font-medium text-gray-900">{document.size}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Version</span>
              <span className="font-medium text-gray-900">v{document.version}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Uploaded By</span>
              <span className="font-medium text-gray-900">{document.uploadedBy}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Upload Date</span>
              <span className="font-medium text-gray-900">{document.uploadDate}</span>
            </div>
          </div>

          {document.tags.length > 0 && (
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-700 mb-2">Tags</p>
              <div className="flex flex-wrap gap-2">
                {document.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <a
              href={document.url}
              download={document.name}
              className="flex-1 px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium text-center cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-download-line mr-2 w-4 h-4 inline-flex items-center justify-center"></i>
              Download
            </a>
            <button
              onClick={() => window.open(document.url, '_blank')}
              className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-external-link-line w-4 h-4 flex items-center justify-center"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
